"use client"

import { useState, useEffect, useRef } from "react"
import Chart from "chart.js/auto"
import { useLanguage } from "../../context/LanguageContext"
import "./WeatherRangeChart.css"

const WeatherRangeChart = ({
  weatherData,
  startDate,
  endDate,
  locationName,
  coordinates,
  selectedCountry,
  selectedDistrict,
  countries,
  districts,
}) => {
  const { t } = useLanguage()
  const chartRef = useRef(null)
  const chartInstance = useRef(null)
  const [chartType, setChartType] = useState("line") // 'line', 'bar', or 'mixed'

  useEffect(() => {
    if (!weatherData || !Array.isArray(weatherData) || weatherData.length === 0) return

    // Destroy previous chart if it exists
    if (chartInstance.current) {
      chartInstance.current.destroy()
    }

    const ctx = chartRef.current.getContext("2d")

    // Prepare data for the chart
    const labels = weatherData.map((day) => {
      const date = new Date(day.date)
      return date.toLocaleDateString("en-US", { month: "short", day: "numeric" })
    })

    // Configure datasets based on chart type - Simple lines without background
    const datasets = [
      {
        label: t("Max Temperature (°C)"),
        data: weatherData.map((day) => day.tempMax || day.temperature),
        borderColor: "#ff6b6b",
        backgroundColor: "transparent", // Remove background
        borderWidth: 2,
        tension: chartType === "bar" ? 0 : 0.4,
        pointRadius: chartType === "bar" ? 0 : 4,
        pointHoverRadius: chartType === "bar" ? 0 : 6,
        pointBackgroundColor: "#ff6b6b",
        pointBorderColor: "#fff",
        pointBorderWidth: 2,
        pointHoverBackgroundColor: "#ff5252",
        pointHoverBorderColor: "#fff",
        pointHoverBorderWidth: 2,
        fill: false, // No fill for simple lines
        type: chartType === "mixed" ? "line" : chartType,
        yAxisID: "y",
      },
      {
        label: t("Min Temperature (°C)"),
        data: weatherData.map((day) => day.tempMin || day.temperature - 5),
        borderColor: "#4a90e2",
        backgroundColor: "transparent", // Remove background
        borderWidth: 2,
        tension: chartType === "bar" ? 0 : 0.4,
        pointRadius: chartType === "bar" ? 0 : 4,
        pointHoverRadius: chartType === "bar" ? 0 : 6,
        pointBackgroundColor: "#4a90e2",
        pointBorderColor: "#fff",
        pointBorderWidth: 2,
        pointHoverBackgroundColor: "#2196f3",
        pointHoverBorderColor: "#fff",
        pointHoverBorderWidth: 2,
        fill: false, // No fill for simple lines
        type: chartType === "mixed" ? "line" : chartType,
        yAxisID: "y",
      },
      {
        label: t("Humidity (%)"),
        data: weatherData.map((day) => day.humidity),
        borderColor: "#2dce89",
        backgroundColor: chartType === "line" ? "transparent" : "rgba(45, 206, 137, 0.7)", // Only for bar charts
        borderWidth: 2,
        tension: chartType === "bar" ? 0 : 0.4,
        pointRadius: chartType === "bar" ? 0 : 4,
        pointHoverRadius: chartType === "bar" ? 0 : 6,
        pointBackgroundColor: "#2dce89",
        pointBorderColor: "#fff",
        pointBorderWidth: 2,
        pointHoverBackgroundColor: "#4caf50",
        pointHoverBorderColor: "#fff",
        pointHoverBorderWidth: 2,
        fill: false, // No fill for simple lines
        type: chartType === "mixed" ? "bar" : chartType,
        yAxisID: "y1",
      },
      {
        label: t("Wind Speed (m/s)"),
        data: weatherData.map((day) => day.windSpeed),
        borderColor: "#9b59b6",
        backgroundColor: chartType === "line" ? "transparent" : "rgba(155, 89, 182, 0.7)", // Only for bar charts
        borderWidth: 2,
        tension: chartType === "bar" ? 0 : 0.4,
        pointRadius: chartType === "bar" ? 0 : 4,
        pointHoverRadius: chartType === "bar" ? 0 : 6,
        pointBackgroundColor: "#9b59b6",
        pointBorderColor: "#fff",
        pointBorderWidth: 2,
        pointHoverBackgroundColor: "#8e44ad",
        pointHoverBorderColor: "#fff",
        pointHoverBorderWidth: 2,
        fill: false, // No fill for simple lines
        type: chartType === "mixed" ? "bar" : chartType,
        yAxisID: "y2",
      },
    ]

    // Create the chart
    chartInstance.current = new Chart(ctx, {
      type: chartType === "mixed" ? "bar" : chartType,
      data: {
        labels,
        datasets,
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: "index",
          intersect: false,
        },
        plugins: {
          legend: {
            position: "top",
            labels: {
              usePointStyle: true,
              pointStyle: "circle",
              padding: 15,
              font: {
                size: 11,
                weight: "600",
                family: "'Inter', sans-serif",
              },
              color: "#2d3748",
            },
          },
          tooltip: {
            backgroundColor: "rgba(45, 55, 72, 0.95)",
            titleColor: "#fff",
            bodyColor: "#fff",
            borderColor: "rgba(255, 255, 255, 0.1)",
            borderWidth: 1,
            cornerRadius: 8,
            displayColors: true,
            titleFont: {
              size: 12,
              weight: "600",
            },
            bodyFont: {
              size: 11,
              weight: "500",
            },
            padding: 8,
            callbacks: {
              title: (context) => {
                const date = new Date(weatherData[context[0].dataIndex].date)
                return date.toLocaleDateString("en-US", {
                  weekday: "long",
                  month: "long",
                  day: "numeric",
                })
              },
              label: (context) => {
                const label = context.dataset.label
                const value = context.raw
                if (label.includes("Temperature")) {
                  return `${label}: ${value}°C`
                } else if (label.includes("Humidity")) {
                  return `${label}: ${value}%`
                } else if (label.includes("Wind")) {
                  return `${label}: ${value} m/s`
                }
                return `${label}: ${value}`
              },
            },
          },
        },
        scales: {
          x: {
            title: {
              display: true,
              text: t("Date"),
              font: {
                size: 12,
                weight: "600",
                family: "'Inter', sans-serif",
              },
              color: "#4a5568",
            },
            grid: {
              color: "rgba(226, 232, 240, 0.4)",
              lineWidth: 1,
            },
            ticks: {
              font: {
                size: 10,
                weight: "500",
              },
              color: "#718096",
              padding: 4,
            },
          },
          y: {
            type: "linear",
            display: true,
            position: "left",
            title: {
              display: true,
              text: t("Temperature (°C)"),
              color: "#ff6b6b",
              font: {
                size: 11,
                weight: "600",
                family: "'Inter', sans-serif",
              },
            },
            grid: {
              color: "rgba(226, 232, 240, 0.4)",
              lineWidth: 1,
            },
            ticks: {
              color: "#ff6b6b",
              font: {
                size: 10,
                weight: "500",
              },
              padding: 4,
            },
          },
          y1: {
            type: "linear",
            display: true,
            position: "right",
            title: {
              display: true,
              text: t("Humidity (%)"),
              color: "#2dce89",
              font: {
                size: 11,
                weight: "600",
                family: "'Inter', sans-serif",
              },
            },
            grid: {
              drawOnChartArea: false,
            },
            ticks: {
              color: "#2dce89",
              font: {
                size: 10,
                weight: "500",
              },
              padding: 4,
            },
            max: 100,
            min: 0,
          },
          y2: {
            type: "linear",
            display: false,
            position: "right",
            max: Math.max(...weatherData.map((d) => d.windSpeed)) * 1.2,
            min: 0,
          },
        },
      },
    })

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy()
      }
    }
  }, [weatherData, t, chartType])

  if (!weatherData || !Array.isArray(weatherData) || weatherData.length === 0) {
    return (
      <div className="weather-range-chart-container">
        <div className="no-data">
          <div className="no-data-icon">
            <i className="fas fa-chart-line"></i>
          </div>
          <h3>{t("No Data Available")}</h3>
          <p>{t("No weather data available for the selected date range")}</p>
        </div>
      </div>
    )
  }

  // Calculate statistics
  const stats = {
    avgTemp: weatherData.reduce((sum, day) => sum + (day.tempMax || day.temperature), 0) / weatherData.length,
    minTemp: Math.min(...weatherData.map((day) => day.tempMin || day.temperature - 5)),
    maxTemp: Math.max(...weatherData.map((day) => day.tempMax || day.temperature)),
    avgHumidity: weatherData.reduce((sum, day) => sum + day.humidity, 0) / weatherData.length,
    avgWindSpeed: weatherData.reduce((sum, day) => sum + day.windSpeed, 0) / weatherData.length,
    totalDays: weatherData.length,
  }

  // Get location display name
  const getLocationName = () => {
    if (locationName) return locationName
    if (coordinates) return `${coordinates.lat.toFixed(4)}, ${coordinates.lng.toFixed(4)}`
    if (selectedCountry && selectedDistrict) {
      const country = countries.find((c) => c._id === selectedCountry)
      const district = districts.find((d) => d._id === selectedDistrict)
      return `${district?.name || ""}, ${country?.name || ""}`
    }
    return t("Unknown Location")
  }

  return (
    <div className="weather-range-chart-container">
      {/* Header Section */}
      <div className="weather-range-header">
        <div className="header-content">
          <div className="location-date-info">
            <div className="location-info">
              <div className="location-icon">
                <i className="fas fa-map-marker-alt"></i>
              </div>
              <div className="location-text">
                <h2>{getLocationName()}</h2>
                <span className="location-subtitle">{t("Weather Analysis")}</span>
              </div>
            </div>

            <div className="date-info">
              <div className="date-icon">
                <i className="fas fa-calendar-alt"></i>
              </div>
              <div className="date-text">
                <div className="date-range">
                  {new Date(startDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })} -{" "}
                  {new Date(endDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                </div>
                <span className="date-subtitle">
                  {stats.totalDays} {t("days period")}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="weather-stats-grid">
        <div className="stat-card temperature">
          <div className="stat-icon">
            <i className="fas fa-thermometer-half"></i>
          </div>
          <div className="stat-content">
            <div className="stat-main">
              <span className="stat-value">{stats.avgTemp.toFixed(1)}</span>
              <span className="stat-unit">°C</span>
            </div>
            <div className="stat-label">{t("Average Temperature")}</div>
            <div className="stat-range">
              {stats.minTemp.toFixed(1)}° - {stats.maxTemp.toFixed(1)}°
            </div>
          </div>
        </div>

        <div className="stat-card humidity">
          <div className="stat-icon">
            <i className="fas fa-tint"></i>
          </div>
          <div className="stat-content">
            <div className="stat-main">
              <span className="stat-value">{stats.avgHumidity.toFixed(1)}</span>
              <span className="stat-unit">%</span>
            </div>
            <div className="stat-label">{t("Average Humidity")}</div>
            <div className="stat-range">{t("Moisture Level")}</div>
          </div>
        </div>

        <div className="stat-card wind">
          <div className="stat-icon">
            <i className="fas fa-wind"></i>
          </div>
          <div className="stat-content">
            <div className="stat-main">
              <span className="stat-value">{stats.avgWindSpeed.toFixed(1)}</span>
              <span className="stat-unit">m/s</span>
            </div>
            <div className="stat-label">{t("Average Wind Speed")}</div>
            <div className="stat-range">{t("Air Movement")}</div>
          </div>
        </div>

        <div className="stat-card days">
          <div className="stat-icon">
            <i className="fas fa-calendar-day"></i>
          </div>
          <div className="stat-content">
            <div className="stat-main">
              <span className="stat-value">{stats.totalDays}</span>
              <span className="stat-unit">{t("days")}</span>
            </div>
            <div className="stat-label">{t("Analysis Period")}</div>
            <div className="stat-range">{t("Data Points")}</div>
          </div>
        </div>
      </div>

      {/* Chart Section */}
      <div className="chart-section">
        <div className="chart-header">
          <h3>
            <i className="fas fa-chart-line"></i>
            {t("Weather Trends & Analysis")}
          </h3>
          <div className="chart-subtitle">{t("Detailed weather patterns over the selected period")}</div>

          {/* Chart Type Selector */}
          <div className="chart-type-selector">
            <button
              className={`chart-type-btn ${chartType === "line" ? "active" : ""}`}
              onClick={() => setChartType("line")}
              title={t("Line Chart")}
            >
              <i className="fas fa-chart-line"></i>
            </button>
            <button
              className={`chart-type-btn ${chartType === "bar" ? "active" : ""}`}
              onClick={() => setChartType("bar")}
              title={t("Bar Chart")}
            >
              <i className="fas fa-chart-bar"></i>
            </button>
            <button
              className={`chart-type-btn ${chartType === "mixed" ? "active" : ""}`}
              onClick={() => setChartType("mixed")}
              title={t("Mixed Chart")}
            >
              <i className="fas fa-chart-pie"></i>
            </button>
          </div>
        </div>

        <div className="chart-container">
          <canvas ref={chartRef}></canvas>
        </div>
      </div>
    </div>
  )
}

export default WeatherRangeChart
